import React, { memo, useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';

import { AvatarGroup, Box } from '@mui/material';
import PropTypes from 'prop-types';

import { getUuid } from 'store/auth/selectors';
import { startFollowingWithNavigationAction } from 'store/loanApplications/actions';
import { selectEntityNameMap } from 'store/loanApplications/selectors';
import { stopFollowingAction } from 'store/presence/actions';
import {
  selectFollowingUserId,
  selectPresenceUsers,
  selectPresenceUsersForEntity,
} from 'store/presence/selectors';

import { userPropTypes } from '../UsersAvatarGroup/UserCard/UserCard.constants';

import PresenceAvatar from './components/PresenceAvatar';
import PresenceSurplusCount from './components/PresenceSurplusCount';
import { DEFAULT_MAX_AVATARS } from './PresenceAvatarGroup.constants';
import {
  avatarGroupSlotProps,
  avatarGroupStyles,
  wrapperStyles,
} from './PresenceAvatarGroup.styles';
import { mergeAssignedAndPresenceUsers } from './PresenceAvatarGroup.utils';

const PresenceAvatarGroup = ({
  usersData = [],
  maxAvatars = DEFAULT_MAX_AVATARS,
  excludeCurrentUser = false,
  entityRef = null,
}) => {
  const dispatch = useDispatch();
  const { applicationId } = useParams();

  const presenceUsers = useSelector(
    entityRef ? selectPresenceUsersForEntity(entityRef) : selectPresenceUsers,
  );
  const followingUserId = useSelector(selectFollowingUserId);
  const currentUserId = useSelector(getUuid);
  const entityNameMap = useSelector(selectEntityNameMap);

  const mergedUsers = useMemo(
    () =>
      mergeAssignedAndPresenceUsers(usersData, presenceUsers, currentUserId, {
        excludeCurrentUser,
      }),
    [usersData, presenceUsers, currentUserId, excludeCurrentUser],
  );

  const onAvatarClick = useCallback(
    ({ userId, connectionId }) => {
      if (userId === currentUserId) {
        return;
      }

      if (userId === followingUserId) {
        dispatch(stopFollowingAction());

        return;
      }

      dispatch(startFollowingWithNavigationAction({ userId, connectionId, applicationId }));
    },
    [dispatch, followingUserId, currentUserId, applicationId],
  );

  const renderSurplus = useCallback(
    surplus => <PresenceSurplusCount surplus={surplus} usersData={mergedUsers.slice(-surplus)} />,
    [mergedUsers],
  );

  return (
    <Box sx={wrapperStyles}>
      <AvatarGroup
        max={maxAvatars}
        renderSurplus={renderSurplus}
        slotProps={avatarGroupSlotProps}
        spacing={0}
        sx={avatarGroupStyles}
      >
        {mergedUsers.map(user => (
          <PresenceAvatar
            entityNameMap={entityNameMap}
            key={user.userId ?? `${user.firstName}-${user.lastName}`}
            presenceData={user.presenceData}
            userData={user}
            onAvatarClick={onAvatarClick}
          />
        ))}
      </AvatarGroup>
    </Box>
  );
};

PresenceAvatarGroup.propTypes = {
  usersData: PropTypes.arrayOf(PropTypes.shape(userPropTypes)).isRequired,
  maxAvatars: PropTypes.number,
  excludeCurrentUser: PropTypes.bool,
  entityRef: PropTypes.string,
};

export default memo(PresenceAvatarGroup);
